import React, { useState } from 'react';
import { Building2, FileText, Zap, Loader2, ArrowRight, ArrowLeft, Check, ClipboardType, Upload, Sparkles, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const STEPS = [
  { n: 1, label: 'Name project', icon: Building2 },
  { n: 2, label: 'Contract scope', icon: FileText },
  { n: 3, label: 'First communication', icon: Zap },
];

/**
 * First-run 3-step wizard for busy PMs.
 * 1) Name the project (+ notice period)  2) Paste the contract scope  3) Add first communication.
 */
export const OnboardingWizard = ({ open, onOpenChange, onComplete }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [projectId, setProjectId] = useState(null);
  const [name, setName] = useState('');
  const [client, setClient] = useState('');
  const [noticeDays, setNoticeDays] = useState('14');
  const [scope, setScope] = useState('');
  const [scopeTab, setScopeTab] = useState('paste');
  const [scopeFile, setScopeFile] = useState(null);
  const [tab, setTab] = useState('paste');
  const [content, setContent] = useState('');
  const [file, setFile] = useState(null);
  const [captured, setCaptured] = useState(false);
  const [scopeErr, setScopeErr] = useState('');

  const reset = () => {
    setStep(1); setProjectId(null); setName(''); setClient(''); setNoticeDays('14');
    setScope(''); setScopeTab('paste'); setScopeFile(null); setContent(''); setFile(null); setCaptured(false); setScopeErr('');
  };

  const close = (o) => { onOpenChange(o); if (!o) setTimeout(reset, 200); };

  const createProject = async () => {
    if (!name.trim()) { toast.error('Give your project a name'); return; }
    setScopeErr('');
    setSaving(true);
    try {
      // Baseline scope can be pasted OR uploaded (work order / BOQ / contract).
      let scopeText = scope.trim();
      if (scopeTab === 'file') {
        if (!scopeFile) { toast.error('Upload the work order / BOQ, or paste the scope instead'); setSaving(false); return; }
        const fd = new FormData();
        fd.append('file', scopeFile);
        let ex;
        try {
          ex = await api.post('/projects/extract-scope', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch (err) {
          const msg = err?.response?.data?.detail
            || 'Could not read text from that file. If it is a scanned/image PDF, upload a searchable PDF or paste the text instead.';
          setScopeErr(msg);
          setSaving(false);
          return;
        }
        scopeText = (ex.data.text || '').trim();
        if (!scopeText) {
          setScopeErr('No selectable text found in that document (it is likely scanned / image-only). OCR is not supported — upload a searchable PDF or paste the text instead.');
          setSaving(false);
          return;
        }
      }
      if (!scopeText) { toast.error('Add the scope so Recoup has a baseline — paste it or upload the document'); setSaving(false); return; }
      const r = await api.post('/projects', {
        name, client, original_scope: scopeText,
        notice_period_days: parseInt(noticeDays, 10) || 14,
      });
      setProjectId(r.data.id);
      setStep(3);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create project'); }
    finally { setSaving(false); }
  };

  const captureFirst = async () => {
    setSaving(true);
    try {
      if (tab === 'paste') {
        if (!content.trim()) { toast.error('Paste a communication or skip this step'); setSaving(false); return; }
        await api.post('/detections/analyze', { project_id: projectId, source_type: 'email', content });
      } else {
        if (!file) { toast.error('Choose a file or skip this step'); setSaving(false); return; }
        const fd = new FormData();
        fd.append('file', file); fd.append('project_id', projectId); fd.append('source_type', 'email');
        await api.post('/events/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setCaptured(true);
      toast.success('All set — your project is ready.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not read that communication'); }
    finally { setSaving(false); }
  };

  const finish = () => { close(false); onComplete && onComplete(projectId); if (projectId) navigate(`/projects/${projectId}`); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-xl p-0 overflow-hidden" data-testid="onboarding-wizard">
        {/* Header / stepper */}
        <div className="bg-ink-900 text-white px-6 py-5">
          <h3 className="text-lg font-semibold font-display">Set up your first project</h3>
          <p className="text-xs text-slate-300 mt-0.5">Three quick steps and Recoup starts finding opportunities for you.</p>
          <div className="flex items-center gap-2 mt-4">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.n}>
                <div className={cn('flex items-center gap-2 text-xs font-medium', step >= s.n ? 'text-white' : 'text-slate-500')}>
                  <div className={cn('h-6 w-6 rounded-full flex items-center justify-center ring-1',
                    step > s.n ? 'bg-money text-white ring-money' : step === s.n ? 'bg-white/10 text-white ring-white/30' : 'ring-white/10 text-slate-500')}>
                    {step > s.n ? <Check className="h-3.5 w-3.5" /> : s.n}
                  </div>
                  <span className="hidden sm:inline">{s.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={cn('h-px flex-1', step > s.n ? 'bg-money' : 'bg-white/10')} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="px-6 py-5">
          {/* Step 1 */}
          {step === 1 && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Project name *</Label>
                <Input className="mt-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Downtown Lab Fitout" data-testid="onb-name" autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Client (optional)</Label>
                  <Input className="mt-1" value={client} onChange={(e) => setClient(e.target.value)} placeholder="e.g. Acme Developers" data-testid="onb-client" />
                </div>
                <div>
                  <Label className="text-xs">Notice period (days)</Label>
                  <Input className="mt-1" type="number" min="1" value={noticeDays} onChange={(e) => setNoticeDays(e.target.value)} data-testid="onb-notice" />
                </div>
              </div>
              <p className="text-[11px] text-slate-400">Notice period is a project setting you configure — not read from the contract. Recoup counts down to it, but you must confirm each opportunity&rsquo;s deadline against your contract before it&rsquo;s treated as verified.</p>
            </div>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <div className="space-y-2">
              <Label className="text-xs">Work order / BOQ / contract scope *</Label>
              <Tabs value={scopeTab} onValueChange={setScopeTab} className="mt-1">
                <TabsList className="w-full">
                  <TabsTrigger value="paste" className="flex-1" data-testid="onb-scope-tab-paste"><ClipboardType className="h-4 w-4 mr-1.5" />Paste text</TabsTrigger>
                  <TabsTrigger value="file" className="flex-1" data-testid="onb-scope-tab-file"><FileText className="h-4 w-4 mr-1.5" />Upload file</TabsTrigger>
                </TabsList>
                <TabsContent value="paste" className="mt-3">
                  <Textarea rows={6} value={scope} onChange={(e) => setScope(e.target.value)} data-testid="onb-scope"
                    placeholder="Paste the work order, BOQ or contract scope. Spell out inclusions and — crucially — EXCLUSIONS." />
                </TabsContent>
                <TabsContent value="file" className="mt-3">
                  <input id="onb-scope-file" type="file" accept=".pdf,.docx,.eml,.msg,.txt" className="hidden"
                    onChange={(e) => { setScopeFile(e.target.files?.[0] || null); setScopeErr(''); }} data-testid="onb-scope-file-input" />
                  <label htmlFor="onb-scope-file" className="block w-full rounded-xl border-2 border-dashed border-slate-200 hover:border-money/40 bg-slate-50 px-4 py-6 text-center cursor-pointer transition-colors">
                    {scopeFile ? <CheckCircle2 className="h-5 w-5 mx-auto text-money-600" /> : <Upload className="h-5 w-5 mx-auto text-slate-400" />}
                    <div className="text-sm text-slate-600 mt-2">{scopeFile ? scopeFile.name : 'Click to upload work order / BOQ'}</div>
                    <div className="text-[11px] text-slate-400 mt-1">Searchable PDF, DOCX, EML, MSG, TXT — Recoup reads it automatically. Scanned/image PDFs are not supported.</div>
                  </label>
                  {scopeErr && (
                    <div className="mt-3 rounded-lg border border-danger-200 bg-danger-50 p-3 flex gap-2" data-testid="onb-scope-error">
                      <AlertTriangle className="h-4 w-4 text-danger-600 shrink-0 mt-0.5" />
                      <div className="text-xs text-danger-700">
                        <div className="font-semibold">This file couldn&rsquo;t be read</div>
                        <p className="mt-0.5">{scopeErr}</p>
                      </div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
              <p className="text-[11px] text-slate-400">Tip: the sharper the scope (especially exclusions), the better Recoup spots extra work.</p>
            </div>
          )}

          {/* Step 3 */}
          {step === 3 && (
            captured ? (
              <div className="flex flex-col items-center text-center py-6" data-testid="onb-done">
                <div className="rounded-2xl bg-money-50 ring-1 ring-money-100 p-3 mb-3"><CheckCircle2 className="h-7 w-7 text-money-600" /></div>
                <div className="text-base font-semibold text-ink-900">You&rsquo;re all set</div>
                <p className="text-sm text-slate-500 mt-1 max-w-sm">Recoup has reviewed your first communication. Open the project to see what it found.</p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">Add one communication to review now — or skip and add things anytime.</p>
                <Tabs value={tab} onValueChange={setTab}>
                  <TabsList className="w-full">
                    <TabsTrigger value="paste" className="flex-1" data-testid="onb-tab-paste"><ClipboardType className="h-4 w-4 mr-1.5" />Paste text</TabsTrigger>
                    <TabsTrigger value="file" className="flex-1" data-testid="onb-tab-file"><FileText className="h-4 w-4 mr-1.5" />Upload file</TabsTrigger>
                  </TabsList>
                  <TabsContent value="paste" className="mt-3">
                    <Textarea rows={5} value={content} onChange={(e) => setContent(e.target.value)} placeholder="Paste an email, RFI, site instruction or meeting note..." data-testid="onb-content" />
                  </TabsContent>
                  <TabsContent value="file" className="mt-3">
                    <input id="onb-file" type="file" accept=".pdf,.docx,.eml,.msg,.txt" className="hidden"
                      onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="onb-file-input" />
                    <label htmlFor="onb-file" className="block w-full rounded-xl border-2 border-dashed border-slate-200 hover:border-money/40 bg-slate-50 px-4 py-6 text-center cursor-pointer transition-colors">
                      <Upload className="h-5 w-5 mx-auto text-slate-400" />
                      <div className="text-sm text-slate-600 mt-2">{file ? file.name : 'Click to choose a file'}</div>
                      <div className="text-[11px] text-slate-400 mt-1">PDF, DOCX, EML, MSG, TXT</div>
                    </label>
                  </TabsContent>
                </Tabs>
              </div>
            )
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
          <div>
            {step > 1 && !captured && (
              <Button variant="ghost" onClick={() => setStep(step - 1)} data-testid="onb-back"><ArrowLeft className="h-4 w-4 mr-1.5" />Back</Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {step === 1 && (
              <Button className="bg-primary hover:bg-ink-700" onClick={() => (name.trim() ? setStep(2) : toast.error('Give your project a name'))} data-testid="onb-next-1">
                Next <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            )}
            {step === 2 && (
              <Button className="bg-money hover:bg-money-700" disabled={saving} onClick={createProject} data-testid="onb-create">
                {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-1.5" />}Create &amp; continue
              </Button>
            )}
            {step === 3 && !captured && (
              <>
                <Button variant="outline" onClick={finish} data-testid="onb-skip">Skip for now</Button>
                <Button className="bg-money hover:bg-money-700" disabled={saving} onClick={captureFirst} data-testid="onb-capture">
                  {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}Review it
                </Button>
              </>
            )}
            {step === 3 && captured && (
              <Button className="bg-money hover:bg-money-700" onClick={finish} data-testid="onb-finish">
                Open project <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
